import React from 'react';
import PropTypes from 'prop-types';

import responseReader from '../../store/readHelpers/ResponseReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import { t } from '../../services/i18nService';
import navigationService from '../../services/NavigationService';
import objectTypes from '../../constants/ObjectTypes';

import styles from './footer.scss';
import SectionLabel from './SectionLabel';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import footerService from '../../services/FooterService';
import customFieldsService from '../../services/CustomFieldsService';
import customFieldsConstants from '../../constants/CustomFieldsConstants';
import questionReader from '../../store/readHelpers/QuestionReader';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import reloginService from '../../services/ReloginService';
import questionSelectionService from '../../services/QuestionSelectionService';
import postMessageService from '../../services/PostMessageService';
import domService from '../../services/DomService';
import assessmentService from '../../services/AssessmentService';
import footerSelector from '../../selectors/footer/FooterSelector';
import MandatoryModal from '../modal/MandatoryModal';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import responseService from '../../services/ResponseService';

class FooterNavRow extends React.PureComponent {
    static propTypes = {
        assessment: PropTypes.object.isRequired,
        currentlyShown: PropTypes.object.isRequired,
        flaggedItems: PropTypes.array.isRequired,
        isNavigationAllowed: PropTypes.bool.isRequired,
        responseStatus: PropTypes.object.isRequired,
        settingValues: PropTypes.object,
        showFinalStepButton: PropTypes.bool.isRequired,
        ui: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            showMandatoryModal: false,
            navigationType: null,
            pendingContentRevisionId: null,
            pendingSectionId: null,
        };
    }

    handleReturnToQuestion = () => {
        this.setState({
            showMandatoryModal: false,
            navigationType: null,
            pendingContentRevisionId: null,
            pendingSectionId: null,
        });
    };

    handleConfirmNext = () => {
        const { navigationType, pendingContentRevisionId, pendingSectionId } = this.state;

        this.setState(
            {
                showMandatoryModal: false,
                navigationType: null,
                pendingContentRevisionId: null,
                pendingSectionId: null,
            },
            () => {
                if (navigationType === 'finalStep') {
                    navigationService.navigateToFinalStep();
                } else if (navigationType === 'question' && pendingContentRevisionId && pendingSectionId) {
                    this.executePendingNavigation(pendingContentRevisionId, pendingSectionId);
                }
            }
        );
    };

    executePendingNavigation = (contentRevisionId, sectionId) => {
        const { currentlyShown } = this.props;
        if (currentlyShown?.questionId && contentRevisionId && reloginService.shouldStoreNextQuestion() &&
        reloginService.shouldStoreNextQuestionIdIfHorizontalLayoutConditions()) {
            reloginService.storeNextQuestionIdFlag(currentlyShown.questionId, contentRevisionId);
        }

        this.sendNavigationDataToParent({
            contentRevisionId,
            sectionId,
        });

        navigationService.navigateToQuestion(contentRevisionId, sectionId);
    };

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.navItems = this.getNavItems(this.props);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        this.navItems = this.getNavItems(nextProps);
    }

    getTitleOrFallback = (question, fallback) => {
        const showTitle = settingsReader.isShowQuestionTitlesEnabled();
        if (showTitle && question.title) {
            return question.title;
        }
        return fallback;
    };

    getQuestionLabel = (question, questionNumber) => {
        if (question.objectType === objectTypes.FORM) {
            return <i className="fa fa-check-square-o" aria-hidden="true" />;
        } else if (question.objectType === objectTypes.CONTENT_BLOCK) {
            return <i className="fa fa-file-text-o" aria-hidden="true" />;
        } else if (question.objectType === objectTypes.DOCUMENT || !question.questionNumberingInSection) {
            return <i className="fa fa-info" aria-hidden="true" />;
        } else if (question.subQuestionNumber) {
            return `${questionNumber}(${question.subQuestionNumber})`;
        }
        return questionNumber;
    };

    getSectionLabel = (section, ui, isCurrentSection, useNumberPerInteraction, isCSCBSection) => {
        if (section.questions.length === 1) {
            if (section.questions[0].objectType === objectTypes.FORM) {
                return <i className="fa fa-check-square-o" aria-hidden="true" />;
            } else if (section.questions[0].objectType === objectTypes.DOCUMENT) {
                return <i className="fa fa-info" aria-hidden="true" />;
            }
        }

        if (section.questions[0].sectionNumber === -1) {
            return <i className="fa fa-info-circle" aria-hidden="true" />;
        }

        const showSectionPartPrefix = ui.variables.footer__sectionPartPrefix;
        let sectionNumber = section.questions[0].sectionNumber;
        if (section.customFields) {
            sectionNumber = customFieldsService.getCustomFieldValue(section.customFields, customFieldsConstants.SECTION);
        }

        const { responseStatus } = this.props;
        const { attempted, total } = footerSelector.getAttemptsStatusForSection(responseStatus, section, useNumberPerInteraction);
        const allAttempted = footerService.getSectionAttempted(section);

        return (
            <SectionLabel
                showSectionPartPrefix={showSectionPartPrefix}
                sectionNumber={sectionNumber}
                allAttempted={allAttempted}
                isCurrentSection={isCurrentSection}
                attempted={attempted}
                total={total}
                isCSCBSection={isCSCBSection}
            />);
    };

    generateSectionedQuestionsItems = (props) => {
        const { assessment, flaggedItems, currentlyShown, responseStatus, ui } = props;
        const sections = assessment.mergedSections ? assessment.mergedSections : assessment.sections;

        if (sections) {
            return sections.map((section) => {
                const numberOfQuestions = section.questions.length;
                const isCurrentSection = currentlyShown.sectionId === section.id ||
                  (section.mergedSectionIds && section.mergedSectionIds.includes(currentlyShown.sectionId));
                const useNumberPerInteraction = scorableItemNumberingService.useNumberPerInteraction();
                const isCSCBSection = questionSelectionService.isCandidateSelectedContentBlockEnabled();
                let sectionContainsFlaggedItems = section.questions.some(
                    question => flaggedItems.includes(`${question.contentRevisionId}`),
                );

                if (useNumberPerInteraction) {
                    const scorableItemsInSection = scorableItemNumberingService.getScorableItemsInSection(section);
                    sectionContainsFlaggedItems = scorableItemsInSection.some((item) => {
                        return flaggedItems.includes(`${item.contentRevisionId}-${item.orderNumber}`);
                    });
                }

                let numberOfItemsInQuestion = 0;
                if (numberOfQuestions === 1 && useNumberPerInteraction) {
                    numberOfItemsInQuestion = scorableItemNumberingService
                        .getScorableItemsInQuestion(section.questions[0].contentRevisionId).length;
                }

                const showItems = numberOfQuestions > 1 || numberOfItemsInQuestion > 1;
                let isWritingSection = false;
                if (section.customFields) {
                    isWritingSection = customFieldsService
                        .getCustomFieldValue(
                            section.customFields, customFieldsConstants.TESTING_FOCUS_LEVEL_1_FJORD
                        ) === customFieldsConstants.TEST_TYPE_WRITING;
                }

                const additionalStyles =
                  (isCurrentSection ? ' selected' : '') +
                  (numberOfQuestions > 1 || numberOfItemsInQuestion > 1 ? ' multiple' : ' single') +
                  (footerService.getSectionAttempted(section) ? ' attempted' : '') +
                  (numberOfQuestions > 9 ? ' compact' : '');

                if (numberOfQuestions === 0) {
                    return null;
                }

                const showSubQuestion = (event) => {
                    event.preventDefault();

                    if (useNumberPerInteraction) {
                        const contentRevisionId = parseInt(event.currentTarget.getAttribute('data-contentrevisionid'), 10);
                        const sectionId = parseInt(event.currentTarget.dataset.sectionid, 10);
                        const question = assessmentReader.getQuestion(contentRevisionId);
                        const currentQuestion = assessmentReader.getQuestion(currentlyShown.questionId);

                        if (this.checkMarketPlaceForMandatoryModal() && currentQuestion?.isMandatoryQuestion) {
                            if (currentlyShown?.questionId) {
                                responseService.flushQuestionResponses(currentlyShown.questionId);
                            }

                            if (this.isCurrentQuestionMandatoryAndUnanswered()) {
                                this.setState({
                                    showMandatoryModal: true,
                                    navigationType: 'question',
                                    pendingContentRevisionId: contentRevisionId,
                                    pendingSectionId: sectionId,
                                });
                                return;
                            }
                        }

                        this.sendNavigationDataToParent({
                            contentRevisionId,
                            sectionId,
                        });
                        this.onScorableItemClick(event, question);
                    } else {
                        this.onQuestionClick(event);
                    }
                };

                const getIsAttempted = (scorableItem, question) => {
                    if (scorableItem === undefined) return responseReader.isQuestionAttempted(question);
                    return scorableItem?.isAttempted;
                };

                const buildBtnClassList = ({
                    isActiveItem,
                    isFirstItemInMergedSection,
                    isFlaggedItem,
                    useNumberPerInteraction,
                    isAttempted,
                }) => {
                    let buttonClassList = 'subQuestion';
                    buttonClassList += isActiveItem ? ' active' : '';
                    buttonClassList += isFirstItemInMergedSection ? ' first-of-merged' : '';
                    buttonClassList += isFlaggedItem ? ' flagged' : '';
                    buttonClassList += useNumberPerInteraction ? ' scorable-item' : '';
                    buttonClassList += isAttempted ? ' attempted' : '';
                    return buttonClassList;
                };

                const questionSectionButtonA11yAttrs = footerSelector.getQuestionSectionButtonA11yAttributes(
                    isCurrentSection
                );

                const ariaLabelForQuestionSection = footerSelector.getAriaLabelForQuestionSection(
                    responseStatus,
                    section,
                    useNumberPerInteraction
                );

                const { sectionWrapperRole, sectionButtonRole } = footerSelector.getQuestionSectionWrapperA11yAttributes();

                return (
                    <div
                        className={styles.questionWrapper + additionalStyles}
                        key={section.id}
                        role={sectionWrapperRole}
                        aria-label={ariaLabelForQuestionSection}
                    >
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <button
                            role={sectionButtonRole}
                            className={`${styles.questionNo} ${
                                sectionContainsFlaggedItems && (
                                    !isCurrentSection ||
                            (numberOfQuestions === 1 && !useNumberPerInteraction) ||
                            (useNumberPerInteraction && numberOfItemsInQuestion === 1 && isCurrentSection)
                                ) ? ' flagged' : ''}
                                ${isWritingSection ? ' writing' : ''}`}
                            data-sectionid={section.id}
                            onClick={this.onSectionButtonClick}
                            onKeyPress={this.onSectionButtonClick}
                            tabIndex={section.id === currentlyShown.sectionId ? '-1' : 0}
                            aria-selected={questionSectionButtonA11yAttrs.ariaSelected}
                        >
                            {this.getSectionLabel(section, ui, isCurrentSection, useNumberPerInteraction, isCSCBSection)}
                        </button>

                        {showItems ?
                        /* eslint-disable jsx-a11y/no-static-element-interactions */
                            <div
                                className={styles.subquestionWrapper}
                                onKeyDown={footerSelector.getOnKeyDownForQuestionNavigationInSections()}
                            >
                                {section.questions.map((question) => {
                                    const { contentRevisionId, objectType, questionNumberingInSection } = question;
                                    let isFlaggedItem = flaggedItems.includes(contentRevisionId.toString());
                                    let isActiveItem = currentlyShown.questionId === contentRevisionId;
                                    let canBeAttempted = true;
                                    let questionPrefix = t('generic.questionNumberPrefix');
                                    if (objectType === objectTypes.DOCUMENT) {
                                        questionPrefix = this.getTitleOrFallback(question, t('questionTypes.content_nativehtml'));
                                        canBeAttempted = false;
                                    } else if (objectType === objectTypes.FORM) {
                                        questionPrefix = this.getTitleOrFallback(question, t('generic.questionNumberPrefix'));
                                    } else if (objectType === objectTypes.CONTENT_BLOCK) {
                                        questionPrefix = this.getTitleOrFallback(question, t('questionTypes.content_question_qti2'));
                                        canBeAttempted = false;
                                    }

                                    const getOrderNumber = (scorableItem) => {
                                        if (scorableItem.firstCollapsedItem) {
                                            return scorableItemNumberingService.getInteractionNumber(
                                                contentRevisionId,
                                                scorableItem.responseIdentifier
                                            );
                                        }
                                        return scorableItem.orderNumber;
                                    };

                                    const renderItemElement = (scorableItem, isFirstItemInMergedSection) => {
                                        if (isCSCBSection) return null;
                                        const isAttempted = getIsAttempted(scorableItem, question);

                                        const itemLabel = scorableItem !== undefined
                                            ? getOrderNumber(scorableItem)
                                            : this.getQuestionLabel(
                                                question,
                                                questionNumberingInSection,
                                                numberOfQuestions,
                                            );

                                        const itemKey = scorableItem !== undefined
                                            ? `${contentRevisionId}_${scorableItem.orderNumber}`
                                            : contentRevisionId;

                                        const orderNumber = scorableItem !== undefined
                                            ? scorableItem.orderNumber
                                            : undefined;

                                        if (scorableItem !== undefined) {
                                            isActiveItem = currentlyShown.scorableItem === scorableItem.orderNumber;
                                            isFlaggedItem = flaggedItems.includes(`${contentRevisionId.toString()}-${scorableItem.orderNumber}`);
                                        }

                                        const buttonClassList = buildBtnClassList({
                                            isActiveItem, isFirstItemInMergedSection, isFlaggedItem, useNumberPerInteraction, isAttempted,
                                        });

                                        const attemptedLabel = isAttempted ? t('testOverview.isAttempted') : t('testOverview.notAttempted');

                                        const a11yAttrsForQuestionBtn = footerSelector.getQuestionButtonA11yAttributes(
                                            contentRevisionId,
                                            isActiveItem
                                        );

                                        const ariaLabelForQuestion = footerSelector.getAriaLabelForQuestion(
                                            isAttempted,
                                            questionPrefix,
                                            itemLabel
                                        );

                                        return (
                                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                            <button
                                                className={buttonClassList}
                                                data-contentrevisionid={contentRevisionId}
                                                data-ordernumber={orderNumber}
                                                data-sectionid={question.mergedSectionId ? question.mergedSectionId : section.id}
                                                key={itemKey}
                                                onClick={showSubQuestion}
                                                role={a11yAttrsForQuestionBtn.role}
                                                tabIndex={a11yAttrsForQuestionBtn.tabIndex}
                                                aria-selected={a11yAttrsForQuestionBtn.ariaSelected}
                                                aria-controls={a11yAttrsForQuestionBtn.ariaControls}
                                                aria-current={a11yAttrsForQuestionBtn.ariaCurrent}
                                                aria-label={ariaLabelForQuestion}
                                            >
                                                <span className="sr-only">{questionPrefix} { itemLabel }</span>
                                                <span aria-hidden="true">{ itemLabel }</span>
                                                <span className="sr-only">
                                                    {
                                                        canBeAttempted ? attemptedLabel : ''
                                                    }
                                                    {
                                                        isActiveItem ? ` ${t('testOverview.isActiveQuestion')}` : ''
                                                    }
                                                    {
                                                        isFlaggedItem ? ` ${t('testOverview.flagged')}` : ''
                                                    }
                                                </span>
                                            </button>
                                        );
                                    };

                                    if (useNumberPerInteraction) {
                                        const { scorableItemsStatus = [] } = (responseStatus[contentRevisionId] || {});
                                        const scorableItems = scorableItemNumberingService.getScorableItemsInQuestion(contentRevisionId);

                                        return scorableItems
                                            .filter((item) => {
                                                return !item.hidden;
                                            })
                                            .map((item, index) => {
                                                let isAttempted;
                                                if (item.firstCollapsedItem) {
                                                    const filteredStatusItems = scorableItemsStatus.filter(
                                                        statusItem => statusItem.responseIdentifier === item.responseIdentifier
                                                    );
                                                    isAttempted = filteredStatusItems.length > 0 && filteredStatusItems.every(
                                                        statusItem => statusItem.isAttempted === true
                                                    );
                                                } else {
                                                    isAttempted = scorableItemsStatus.some((statusItem) => {
                                                        return item.orderNumber === statusItem.orderNumber && statusItem.isAttempted === true;
                                                    });
                                                }
                                                const isFirstItemInMergedSection = index === 0 && question.mergedSectionId;
                                                return renderItemElement({ ...item, isAttempted }, isFirstItemInMergedSection);
                                            });
                                    }

                                    return renderItemElement();
                                })}
                            </div>
                            : null
                        }
                    </div>
                );
            });
        }
        return null;
    };

    getQuestionButtonClasses = (isFlagged, isNavigationAllowed) => {
        const questionButtonClasses = [styles.questionNo];
        if (isFlagged) {
            questionButtonClasses.push('flagged');
        }
        if (!isNavigationAllowed) {
            questionButtonClasses.push(styles.noNavigationAllowed);
        }
        return questionButtonClasses;
    };

    generateSequentialDisplayItems = (props) => {
        const {
            assessment,
            currentlyShown,
            flaggedItems,
            isNavigationAllowed,
        } = props;

        let questions = [];
        if (assessment.sections) {
            assessment.sections.map((section) => {
                questions = section.questions.reduce((coll, question) => {
                    question.sectionId = section.id;
                    coll.push(question);
                    return coll;
                }, questions);
                return questions;
            });
            const numberOfQuestions = questions.length;
            if (numberOfQuestions > 0) {
                return questions.map((question) => {
                    const { contentRevisionId } = question;
                    const questionLabel = this.getQuestionLabel(question, question.questionNumberInQuestionSet, numberOfQuestions);
                    const additionalStyles =
                      `${(currentlyShown.questionId === contentRevisionId ? ' selected' : '') +
                      (numberOfQuestions > 1 ? ' multiple' : ' single') +
                      (footerService.isQuestionAttemptedWithAllScorableItems(question) ? ' attempted' : '') +
                      (numberOfQuestions > 9 ? ' compact' : '')} sequential`;
                    const isFlagged = flaggedItems.indexOf(`${contentRevisionId}`) > -1;
                    const isActiveQuestion = currentlyShown.questionId === contentRevisionId;
                    let questionPrefix = t('generic.questionNumberPrefix');
                    let canBeAttempted = true;
                    if (question.objectType === objectTypes.DOCUMENT) {
                        questionPrefix = this.getTitleOrFallback(question, t('questionTypes.content_nativehtml'));
                        canBeAttempted = false;
                    } else if (question.objectType === objectTypes.FORM) {
                        questionPrefix = this.getTitleOrFallback(question, t('generic.questionNumberPrefix'));
                    } else if (question.objectType === objectTypes.CONTENT_BLOCK) {
                        questionPrefix = this.getTitleOrFallback(question, t('questionTypes.content_question_qti2'));
                        canBeAttempted = false;
                    }
                    let title = questionPrefix;
                    if (question.questionNumberInQuestionSet !== undefined) {
                        title = `${title} ${question.questionNumberInQuestionSet}`;
                    }

                    const questionButtonClasses = this.getQuestionButtonClasses(isFlagged, isNavigationAllowed);
                    const attemptedLabel = footerService.isQuestionAttemptedWithAllScorableItems(question)
                        ? t('testOverview.isAttempted')
                        : t('testOverview.notAttempted');
                    const tabIndex = !isNavigationAllowed ? '-1' : undefined;

                    const questionButtonAriaLabelValue = footerSelector.getQuestionButtonAriaLabel(
                        footerService.isQuestionAttemptedWithAllScorableItems(question)
                    );

                    return (
                        <div className={styles.questionWrapper + additionalStyles} key={question.contentRevisionId}>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <button
                                className={questionButtonClasses.join(' ')}
                                data-contentrevisionid={contentRevisionId}
                                data-sectionid={question.sectionId}
                                title={title}
                                tabIndex={tabIndex}
                                onClick={isNavigationAllowed ? this.onQuestionClick : undefined}
                                aria-label={questionButtonAriaLabelValue}
                            >
                                <span className="sr-only">{questionPrefix} { questionLabel }</span>
                                <span aria-hidden="true">{ questionLabel }</span>
                                <span className="sr-only">
                                    {
                                        canBeAttempted ? attemptedLabel : ''
                                    }
                                    {
                                        isActiveQuestion ? ` ${t('testOverview.isActiveQuestion')}` : ''
                                    }
                                    {
                                        isFlagged ? ` ${t('testOverview.flagged')}` : ''
                                    }
                                </span>
                            </button>
                        </div>
                    );
                });
            }
        }
        return null;
    };

    onTocButtonClick = (event) => {
        event.preventDefault();
        navigationService.navigateToToc();
    };

    onFinalStepButtonClick = (event) => {
        event.preventDefault();
        const { currentlyShown } = this.props;
        const question = assessmentReader.getQuestion(currentlyShown.questionId);

        if (this.checkMarketPlaceForMandatoryModal() && question?.isMandatoryQuestion) {
            if (currentlyShown?.questionId) {
                responseService.flushQuestionResponses(currentlyShown.questionId);
            }

            if (this.isCurrentQuestionMandatoryAndUnanswered()) {
                this.setState({
                    showMandatoryModal: true,
                    navigationType: 'finalStep',
                    pendingContentRevisionId: null,
                    pendingSectionId: null,
                });
                return;
            }
        }
        if (!navigationService.isNavigatingAwayFromIncompleteResponsesConfirmed(currentlyShown.questionId)) {
            return;
        }

        navigationService.navigateToFinalStep();
    };

    onSectionButtonClick = (event) => {
        event.preventDefault();
        const sectionId = Number.parseInt(event.currentTarget.dataset.sectionid, 10);
        navigationService.navigateToSection(
            sectionId,
            this.sendNavigationDataToParent,
        );
    };

    sendNavigationDataToParent = ({
        contentRevisionId,
        sectionId,
    }) => {
        if (!domService.windowHasParent()) {
            return;
        }

        const itemId = assessmentService.mapContentRevisionIdToContentId(contentRevisionId, this.props.assessment);

        if (itemId === -1) {
            return;
        }

        postMessageService.postTo(window.parent, {
            type: 'player:navigatedToContent',
            contentRevisionId,
            sectionId,
            itemId,
        });
    }

    isCurrentQuestionMandatoryAndUnanswered = () => {
        if (this.checkMarketPlaceForMandatoryModal()) {
            const { currentlyShown } = this.props;
            const question = assessmentReader.getQuestion(currentlyShown.questionId);

            if (question?.isMandatoryQuestion) {
                return !responseReader.isQuestionDone(question);
            }

            return false;
        }
        return false;
    };

    onQuestionClick = (event) => {
        event.preventDefault();
        const { currentlyShown } = this.props;

        const contentRevisionId = Number.parseInt(event.currentTarget.dataset.contentrevisionid, 10);
        const sectionId = Number.parseInt(event.currentTarget.dataset.sectionid, 10);

        const question = assessmentReader.getQuestion(currentlyShown.questionId);

        if (!question) {
            if (!navigationService.isNavigatingAwayFromIncompleteResponsesConfirmed(currentlyShown.questionId)) {
                return;
            }

            this.sendNavigationDataToParent({ contentRevisionId, sectionId });
            navigationService.navigateToQuestion(contentRevisionId, sectionId);
            return;
        }

        if (this.checkMarketPlaceForMandatoryModal() && question.isMandatoryQuestion) {
            if (currentlyShown?.questionId) {
                responseService.flushQuestionResponses(currentlyShown.questionId);
            }

            if (this.isCurrentQuestionMandatoryAndUnanswered()) {
                this.setState({
                    showMandatoryModal: true,
                    navigationType: 'question',
                    pendingContentRevisionId: contentRevisionId,
                    pendingSectionId: sectionId,
                });
                return;
            }
        }

        if (!navigationService.isNavigatingAwayFromIncompleteResponsesConfirmed(currentlyShown.questionId)) {
            return;
        }

        this.executePendingNavigation(contentRevisionId, sectionId);
    };

    onScorableItemClick = (event, question) => {
        const { currentlyShown } = this.props;
        const sectionId = parseInt(event.currentTarget.getAttribute('data-sectionid'), 10);
        const contentRevisionId = parseInt(event.currentTarget.getAttribute('data-contentrevisionid'), 10);
        const orderNumber = parseInt(event.currentTarget.getAttribute('data-ordernumber'), 10);

        if (currentlyShown?.questionId && contentRevisionId && reloginService.shouldStoreNextQuestion() &&
          reloginService.shouldStoreNextQuestionIdIfHorizontalLayoutConditions()) {
            reloginService.storeNextQuestionIdFlag(currentlyShown.questionId, contentRevisionId);
        }

        if (questionReader.isGapMatchQuestion(question)) {
            // trigger sroll to element after render events
            setTimeout(() => navigationService.scrollToGapInput(contentRevisionId, orderNumber).catch(console.log));
        } else {
            navigationService.scrollToQuestion(sectionId, contentRevisionId, orderNumber, 0, 300);
        }

        // There should be a timeout to execute navigation after scroll event
        // navigationService.scrollToQuestion - this method returns promise, but we can't handle all cases with
        // .then() or finally()
        setTimeout(() => {
            navigationService.navigateToScorableItem(sectionId, contentRevisionId, orderNumber);
        });
    };

    getNavItems = (props) => {
        const scheme = props.settingValues.numbering_scheme;

        if (scheme === 'testSequential') {
            return this.generateSequentialDisplayItems(props);
        }

        return this.generateSectionedQuestionsItems(props);
    };

    getTocButton = () => {
        const { currentlyShown, ui } = this.props;
        const tocButtonIcon = ui.variables.footer__tocButtonIcon;
        let classList = styles.homeButton;
        classList += currentlyShown.route === '/' ? ' active' : '';

        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        return (<button
            id="tableOfContentsButton"
            aria-label={t('navigation.tableOfContents')}
            className={classList}
            onClick={this.onTocButtonClick}
        >
            <i className={`fa ${tocButtonIcon}`} aria-hidden="true" />
        </button>);
    }

    checkMarketPlaceForMandatoryModal = () => {
        return marketplacePropertiesService.isMandatoryQuestionEnabled();
    }

    getFinalStepButton = () => {
        const { assessment, isNavigationAllowed } = this.props;
        const classList = [styles.deliverButton];

        if (navigationService.isOnFinalStepPage()) {
            classList.push('active');
        }

        if (footerService.getAssessmentCompleted(assessment)) {
            classList.push('complete');
        }

        if (!isNavigationAllowed) {
            classList.push(styles.noNavigationAllowed);
        }

        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        return (<button
            id="deliver-button"
            aria-label={t('navigation.deliverButton')}
            className={classList.join(' ')}
            onClick={isNavigationAllowed ? this.onFinalStepButtonClick : null}
        >
            <i className="fa fa fa-check" aria-hidden="true" />
        </button>);
    }

    render() {
        const { settingValues, showFinalStepButton } = this.props;
        const numberingScheme = settingValues.numbering_scheme;
        const tabListIndex = footerSelector.getTabListTabIndex();
        const tabListAriaLabel = footerSelector.getTabListAriaLabel(
            this.props.assessment,
            settingValues
        );
        const navRoleAsTabList = footerSelector.getNavRoleAsTabList();
        const { showMandatoryModal } = this.state;
        // eslint-disable-next-line camelcase
        const isOneWay = !!settingValues?.enable_one_way_navigation;

        const NavRowTag = footerSelector.isA11yV5AndAboveSupported() ? 'div' : 'nav';

        return (
            <NavRowTag
                className={`nav-row ${numberingScheme}`}
                aria-label={tabListAriaLabel}
                tabIndex={tabListIndex}
                role={navRoleAsTabList}
            >
                { settingsReader.showTableOfContents() && this.getTocButton() }
                { this.navItems }
                { showFinalStepButton && this.getFinalStepButton() }
                {showMandatoryModal && this.checkMarketPlaceForMandatoryModal() && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <MandatoryModal
                                show={showMandatoryModal}
                                isOneWay={isOneWay}
                                onReturnToQuestion={this.handleReturnToQuestion}
                                onConfirmNext={this.handleConfirmNext}
                            />
                        </div>
                    </div>
                )}
            </NavRowTag>
        );
    }
}

export default FooterNavRow;
